import {
  FlightsClassTypes,
  labelFareCabinMap,
} from 'constants/flight'
import { nonNullable } from 'lib/array/arrayUtils'
import { getFareClass } from 'lib/flights/flightUtils'
import { BookmarkRequest } from 'tripPlanner/api/bookmark/types'
import {
  BasicTrip,
  TripItemAndOfferAndDatesIdSet,
} from 'tripPlanner/types/common'

type ItemTypes = BookmarkRequest['items'][number]

type FlightPayload = Extract<ItemTypes, { type: 'flight' }>
type HotelPayload = Extract<
  ItemTypes,
  {
    type: 'hotel' | 'bedbank_hotel' | 'tactical_ao_hotel' | 'last_minute_hotel'
  }
>
type AccommodationRentalPayload = Extract<ItemTypes, { type: 'rental' }>
type ExperiencePayload = Extract<ItemTypes, { type: 'experience' }>
type TourPayload = Extract<ItemTypes, { type: 'tour_v2' | 'tour' }>

export const getFlightKey = ({ journeys }: FlightPayload) =>
  nonNullable([
    journeys[0].originAirportCode,
    journeys[0].destinationAirportCode,
    journeys[0].startDate,
  ]).join('_')

export const getExperienceKey = ({ code, startDate }: ExperiencePayload) =>
  nonNullable([code, startDate, startDate]).join('_')

export const getHotelKey = ({ code, startDate, endDate }: HotelPayload) =>
  nonNullable([code, startDate, endDate]).join('_')

export const getAccommodationRentalKey = ({
  code,
  startDate,
  endDate,
}: AccommodationRentalPayload) =>
  nonNullable([code, startDate, endDate]).join('_')

export const getTourKey = ({ code, startDate, endDate }: TourPayload) =>
  nonNullable([code, startDate, endDate]).join('_')

export function getTripItemIdsInCheckout(
  keys: Array<string>,
  idSets: Array<TripItemAndOfferAndDatesIdSet>,
) {
  const itemKeys = new Set(keys)

  const itemIds = idSets
    .filter((s) => itemKeys.has(s.offerIdAndMeta))
    .map((i) => i.itemId)

  return itemIds
}

export const createTripItemKeysFromBookmarkPayloads = (
  payloads: BookmarkRequest['items'],
) =>
  nonNullable(
    payloads.map((payload) => {
      if (
        payload.type === 'bedbank_hotel' ||
        payload.type === 'last_minute_hotel' ||
        payload.type === 'tactical_ao_hotel' ||
        payload.type === 'hotel'
      ) {
        return getHotelKey(payload)
      }
      if (payload.type === 'experience') {
        return getExperienceKey(payload)
      }
      if (payload.type === 'flight') {
        return getFlightKey(payload)
      }
      if (payload.type === 'tour_v2' || payload.type === 'tour') {
        return getTourKey(payload)
      }
      if (payload.type === 'rental') {
        return getAccommodationRentalKey(payload)
      }
    }),
  )

export function offerIdAndMetaToFlightPayload(
  offerIdAndMeta: string,
): FlightPayload {
  const params = Object.fromEntries(new URLSearchParams(offerIdAndMeta))
  params.startDate = params.departDate
  const journey = params as FlightPayload['journeys'][0]
  return {
    journeys: [journey],
  } as FlightPayload
}

export const tripHasItemKeys = (trip: BasicTrip, keys: Array<string>) => {
  const itemKeysInTrip = new Set(
    trip.plannedIdSets.map((s) => {
      return s.offerIdAndMeta.startsWith('?') ?
          getFlightKey(offerIdAndMetaToFlightPayload(s.offerIdAndMeta)) :
        s.offerIdAndMeta
    }),
  )
  return keys.length > 0 && keys.every((key) => itemKeysInTrip.has(key))
}

// External contract FareClass type (excludes 'lux_row')
type ExternalFareClass = 'economy' | 'premium' | 'business' | 'first'

export const mapToFareClass = (fareClass: string): ExternalFareClass => {
  // Map 'lux_row' to 'economy' as the external contract doesn't support 'lux_row'
  if (fareClass === FlightsClassTypes.LUXROW) {
    return 'economy'
  }
  if (fareClass in FlightsClassTypes) {
    return fareClass as ExternalFareClass
  }
  return labelFareCabinMap[getFareClass(fareClass)] as ExternalFareClass
}

// Sometimes flight numbers include the carrier code, sometimes they don't... ಥ_ಥ
export function prependCarrierCodeIfNeeded(
  carrierCode: string,
  flightNumber: string,
): string {
  if (flightNumber.match(/^\d+$/)) {
    return carrierCode + flightNumber
  }
  return flightNumber
}
